import { useState, useRef, useEffect, type KeyboardEvent } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { X, Tag, Plus } from "lucide-react";
import { getTagColor } from "@/lib/utils";

interface TagInputProps {
    tags: string[];
    onChange: (tags: string[]) => void;
    placeholder?: string;
    disabled?: boolean;
    compact?: boolean;
    allTags?: string[]; // All existing tags for autocomplete
}

export function TagInput({
    tags,
    onChange,
    placeholder = "Add tag...",
    disabled = false,
    compact = false,
    allTags = [],
}: TagInputProps) {
    const [inputValue, setInputValue] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);

    // Filter suggestions based on input
    const suggestions = inputValue.trim()
        ? allTags.filter(
              (tag) =>
                  tag.toLowerCase().includes(inputValue.toLowerCase()) &&
                  !tags.includes(tag)
          )
        : [];

    // Reset selection when suggestions change
    useEffect(() => {
        setSelectedSuggestionIndex(0);
    }, [suggestions.length]);

    const normalizeTag = (tag: string) => {
        return tag.toLowerCase().trim().replace(/\s+/g, "-");
    };

    const addTag = (tag: string) => {
        const normalized = normalizeTag(tag);
        if (normalized && !tags.includes(normalized)) {
            onChange([...tags, normalized]);
        }
        setInputValue("");
        setShowSuggestions(false);
    };

    const removeTag = (tagToRemove: string) => {
        onChange(tags.filter((t) => t !== tagToRemove));
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (showSuggestions && suggestions.length > 0) {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setSelectedSuggestionIndex((prev) =>
                    prev < suggestions.length - 1 ? prev + 1 : 0
                );
                return;
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setSelectedSuggestionIndex((prev) =>
                    prev > 0 ? prev - 1 : suggestions.length - 1
                );
                return;
            } else if (e.key === "Enter" || e.key === "Tab") {
                e.preventDefault();
                addTag(suggestions[selectedSuggestionIndex]);
                return;
            }
        }

        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            if (inputValue.trim()) {
                addTag(inputValue);
            }
        } else if (e.key === "Backspace" && !inputValue && tags.length > 0) {
            removeTag(tags[tags.length - 1]);
        } else if (e.key === "Escape") {
            setInputValue("");
            setShowSuggestions(false);
            setIsEditing(false);
        }
    };

    const handleBlur = () => {
        // Delay to allow clicking on suggestions
        setTimeout(() => {
            if (inputValue.trim()) {
                addTag(inputValue);
            }
            setShowSuggestions(false);
            setIsEditing(false);
        }, 150);
    };

    const handleInputChange = (value: string) => {
        setInputValue(value);
        setShowSuggestions(value.trim().length > 0);
    };

    // Compact display mode (just shows tags, click to edit)
    if (compact && !isEditing) {
        return (
            <div
                className="flex flex-wrap items-center gap-1 cursor-pointer group"
                onClick={() => !disabled && setIsEditing(true)}
            >
                {tags.length > 0 ? (
                    tags.map((tag) => {
                        const colors = getTagColor(tag);
                        return (
                            <Badge
                                key={tag}
                                variant="secondary"
                                className={`text-xs px-1.5 py-0 ${colors.bg} ${colors.text}`}
                            >
                                {tag}
                            </Badge>
                        );
                    })
                ) : (
                    <span className="text-xs text-muted-foreground flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Tag className="w-3 h-3" />
                        Add tags
                    </span>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-2 relative">
            <div className="flex flex-wrap items-center gap-1.5 p-2 border rounded-md bg-background min-h-[42px]">
                {tags.map((tag) => {
                    const colors = getTagColor(tag);
                    return (
                        <Badge
                            key={tag}
                            variant="secondary"
                            className={`text-xs px-2 py-0.5 flex items-center gap-1 ${colors.bg} ${colors.text}`}
                        >
                            <Tag className="w-3 h-3" />
                            {tag}
                            {!disabled && (
                                <button
                                    type="button"
                                    onClick={() => removeTag(tag)}
                                    className="ml-0.5 hover:text-destructive transition-colors"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            )}
                        </Badge>
                    );
                })}
                {!disabled && (
                    <Input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={(e) => handleInputChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onBlur={handleBlur}
                        onFocus={() =>
                            inputValue.trim() && setShowSuggestions(true)
                        }
                        placeholder={tags.length === 0 ? placeholder : ""}
                        className="flex-1 min-w-[100px] h-7 border-0 shadow-none focus-visible:ring-0 p-0 text-sm"
                        disabled={disabled}
                        autoFocus={isEditing}
                    />
                )}
            </div>

            {/* Autocomplete suggestions dropdown */}
            {showSuggestions && suggestions.length > 0 && (
                <div
                    ref={suggestionsRef}
                    className="absolute z-50 w-full mt-1 py-1 bg-popover border rounded-md shadow-lg max-h-40 overflow-y-auto"
                >
                    {suggestions.map((suggestion, index) => {
                        const colors = getTagColor(suggestion);
                        return (
                            <button
                                key={suggestion}
                                type="button"
                                className={`w-full px-3 py-1.5 text-left text-sm flex items-center gap-2 hover:bg-accent ${
                                    index === selectedSuggestionIndex
                                        ? "bg-accent"
                                        : ""
                                }`}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    addTag(suggestion);
                                }}
                                onMouseEnter={() =>
                                    setSelectedSuggestionIndex(index)
                                }
                            >
                                <Badge
                                    variant="secondary"
                                    className={`text-xs px-1.5 py-0 ${colors.bg} ${colors.text}`}
                                >
                                    {suggestion}
                                </Badge>
                            </button>
                        );
                    })}
                </div>
            )}

            {!disabled && inputValue && suggestions.length === 0 && (
                <p className="text-xs text-muted-foreground">
                    Press Enter or comma to add new tag
                </p>
            )}
        </div>
    );
}

interface TagDisplayProps {
    tags: string[];
    onClick?: (tag: string) => void;
    size?: "sm" | "default";
}

export function TagDisplay({
    tags,
    onClick,
    size = "default",
}: TagDisplayProps) {
    if (!tags || tags.length === 0) return null;

    const badgeClass =
        size === "sm" ? "text-xs px-1.5 py-0" : "text-xs px-2 py-0.5";

    return (
        <div className="flex flex-wrap items-center gap-1">
            {tags.map((tag) => {
                const colors = getTagColor(tag);
                return (
                    <Badge
                        key={tag}
                        variant="secondary"
                        className={`${badgeClass} ${colors.bg} ${colors.text} ${
                            onClick ? "cursor-pointer hover:opacity-80" : ""
                        }`}
                        onClick={() => onClick?.(tag)}
                    >
                        <Tag className="w-3 h-3 mr-1" />
                        {tag}
                    </Badge>
                );
            })}
        </div>
    );
}
